import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Banknote,
  BadgeCheck,
  Landmark,
  Trash2,
  MessageSquare,
  ShieldCheck,
  User,
  Wallet,
} from 'lucide-react';
import { EMPLOYMENT_STATUS_LABELS, formatLongDate, formatShortDate } from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { PERMISSIONS } from '@unifemga/shared';
import { useAuth } from '@/lib/auth';
import { DeleteMemberModal } from '@/components/DeleteMemberModal';
import { cn, money } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  Skeleton,
  Stat,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { APPLICATION_STATUS, KYC_STATUS, LOAN_STATUS, MEMBER_STATUS } from '@/lib/status';

interface MemberDetail {
  id: string;
  membershipNumber: string;
  name: string;
  email: string;
  phone: string;
  alternatePhone: string | null;
  status: string;
  accountStatus: string;
  kycStatus: string;
  kycApprovedAt: string | null;
  memberSince: string;
  lastLoginAt: string | null;
  borrowingSuspendedUntil: string | null;
  summary: {
    totalBorrowed: string;
    totalRepaid: string;
    outstanding: string;
    activeLoans: number;
    completedLoans: number;
    defaultedLoans: number;
    guaranteeExposure: string;
    guaranteeCount: number;
  };
  bankAccounts: Array<{
    id: string;
    bankName: string;
    accountNumber: string;
    accountName: string;
    isPrimary: boolean;
    verifiedAt: string | null;
  }>;
  loans: Array<{
    id: string;
    loanNumber: string;
    productName: string;
    principal: string;
    outstanding: string;
    status: string;
    daysInArrears: number;
    disbursedAt: string | null;
  }>;
  applications: Array<{
    id: string;
    applicationNumber: string;
    productName: string;
    requestedAmount: string;
    status: string;
    submittedAt: string | null;
  }>;
  guarantees: Array<{
    loanNumber: string;
    outstanding: string;
    status: string;
    confirmedAt: string | null;
  }>;
  notes: Array<{
    id: string;
    body: string;
    isInternal: boolean;
    author: string;
    createdAt: string;
  }>;
}

interface KycDetail {
  status: string;
  fullLegalName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  residentialAddress: string | null;
  city: string | null;
  state: string | null;
  lga: string | null;
  employmentStatus: string | null;
  organisation: string | null;
  jobTitle: string | null;
  monthlyIncomeBand: string | null;
  identificationType: string | null;
  identificationNumberMasked: string | null;
  nextOfKin: {
    fullName: string;
    relationship: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
  } | null;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  documents: Array<{
    id: string;
    documentType: string;
    fileName: string;
    status: string;
    uploadedAt: string;
  }>;
  history: Array<{
    decision: string;
    reason: string | null;
    reviewer: string;
    at: string;
  }>;
}

/**
 * One member's file.
 *
 * The list answers "who is this"; this answers "what have they done and what
 * are they on the hook for" — including the KYC they submitted, which was
 * previously only visible while the profile sat in the review queue and
 * vanished from the interface the moment it was approved.
 */
export default function AdminMemberDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['member', id],
    queryFn: () => api.get<MemberDetail>(`/members/${id}`),
    enabled: Boolean(id),
  });

  // A separate call because KYC lives behind its own permission — a Loan
  // Administrator can see the loan history without seeing the identity file.
  const { data: kyc } = useQuery({
    queryKey: ['member-kyc', id],
    queryFn: () => api.get<KycDetail>(`/kyc/${id}`),
    enabled: Boolean(id),
    retry: false,
  });

  if (isLoading) return <Skeleton className="h-96" />;
  if (!data) {
    return (
      <EmptyState
        icon={<User size={20} />}
        title="Member not found"
        action={<Button onClick={() => navigate('/admin/members')}>Back to members</Button>}
      />
    );
  }

  const suspended =
    data.borrowingSuspendedUntil && new Date(data.borrowingSuspendedUntil) > new Date();

  return (
    <div>
      <Link
        to="/admin/members"
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground-secondary transition-colors hover:text-foreground"
      >
        <ArrowLeft size={15} /> All members
      </Link>

      <PageHeader
        eyebrow={data.membershipNumber}
        title={data.name}
        description={`Member since ${formatLongDate(new Date(data.memberSince))}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge map={MEMBER_STATUS} status={data.status} />
            <StatusBadge map={KYC_STATUS} status={data.kycStatus} />
            {can(PERMISSIONS.MEMBERS_DELETE as never) && (
              <Button size="sm" variant="outline" onClick={() => setConfirmingDelete(true)}>
                <Trash2 size={14} /> Delete
              </Button>
            )}
          </div>
        }
      />

      {suspended && (
        <Alert tone="warning" title="Borrowing suspended" className="mb-6">
          This member cannot take a new loan until{' '}
          {formatLongDate(new Date(data.borrowingSuspendedUntil!))}. Losing access to credit is the
          consequence of arrears here — there is no late fee to charge instead.
        </Alert>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Outstanding"
          value={money(data.summary.outstanding)}
          hint={`${data.summary.activeLoans} active loans`}
          tone={Number(data.summary.outstanding) > 0 ? 'brand' : 'success'}
          icon={<Wallet size={16} />}
        />
        <Stat
          label="Total borrowed"
          value={money(data.summary.totalBorrowed)}
          hint={`${data.summary.completedLoans} completed`}
          icon={<Banknote size={16} />}
        />
        <Stat
          label="Total repaid"
          value={money(data.summary.totalRepaid)}
          tone="success"
          icon={<BadgeCheck size={16} />}
        />
        <Stat
          label="Guarantee exposure"
          value={money(data.summary.guaranteeExposure)}
          hint={`Standing for ${data.summary.guaranteeCount} loans`}
          tone={Number(data.summary.guaranteeExposure) > 0 ? 'warning' : 'neutral'}
          icon={<ShieldCheck size={16} />}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Loans */}
          <Card>
            <CardHeader>
              <CardTitle>Loans</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              {data.loans.length === 0 ? (
                <p className="px-6 pb-6 text-sm text-foreground-secondary">
                  This member has never taken a loan.
                </p>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Loan</Th>
                      <Th>Product</Th>
                      <Th numeric>Principal</Th>
                      <Th numeric>Outstanding</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.loans.map((loan) => (
                      <tr
                        key={loan.id}
                        className="cursor-pointer transition-colors hover:bg-muted/50"
                        onClick={() => navigate(`/admin/loans?q=${loan.loanNumber}`)}
                      >
                        <Td className="numeric font-semibold">{loan.loanNumber}</Td>
                        <Td>{loan.productName}</Td>
                        <Td numeric>{money(loan.principal)}</Td>
                        <Td numeric className={loan.daysInArrears > 0 ? 'font-bold text-danger' : ''}>
                          {money(loan.outstanding)}
                        </Td>
                        <Td>
                          <StatusBadge map={LOAN_STATUS} status={loan.status} />
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Applications */}
          <Card>
            <CardHeader>
              <CardTitle>Applications</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              {data.applications.length === 0 ? (
                <p className="px-6 pb-6 text-sm text-foreground-secondary">No applications.</p>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>Application</Th>
                      <Th>Product</Th>
                      <Th numeric>Requested</Th>
                      <Th>Submitted</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.applications.map((application) => (
                      <tr
                        key={application.id}
                        className="cursor-pointer transition-colors hover:bg-muted/50"
                        onClick={() => navigate(`/admin/applications/${application.id}`)}
                      >
                        <Td className="numeric font-semibold">{application.applicationNumber}</Td>
                        <Td>{application.productName}</Td>
                        <Td numeric>{money(application.requestedAmount)}</Td>
                        <Td className="numeric text-foreground-secondary">
                          {application.submittedAt
                            ? formatShortDate(new Date(application.submittedAt))
                            : '—'}
                        </Td>
                        <Td>
                          <StatusBadge map={APPLICATION_STATUS} status={application.status} />
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardContent>
          </Card>

          <KycPanel kyc={kyc} />

          <NotesPanel memberId={id} notes={data.notes} />
        </div>

        {/* Side column */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              <DetailRow label="Email" value={data.email} />
              <DetailRow label="Telephone" value={data.phone} numeric />
              {data.alternatePhone && (
                <DetailRow label="Alternate" value={data.alternatePhone} numeric />
              )}
              <DetailRow
                label="Last signed in"
                value={data.lastLoginAt ? formatLongDate(new Date(data.lastLoginAt)) : 'Never'}
              />
              <DetailRow label="Account" value={data.accountStatus.toLowerCase()} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Bank accounts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              {data.bankAccounts.length === 0 ? (
                <p className="text-sm text-foreground-secondary">
                  None on file. A loan cannot be disbursed without one.
                </p>
              ) : (
                data.bankAccounts.map((account) => (
                  <div key={account.id} className="rounded-xl border border-border p-3.5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                        <Landmark size={14} className="text-foreground-secondary" />
                        {account.bankName}
                      </p>
                      {account.isPrimary && (
                        <span className="rounded-full bg-primary-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-soft-foreground">
                          Primary
                        </span>
                      )}
                    </div>
                    <p className="numeric mt-1 text-sm text-foreground">{account.accountNumber}</p>
                    <p className="text-xs text-foreground-secondary">{account.accountName}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {data.guarantees.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Standing as guarantor</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                {data.guarantees.map((guarantee) => (
                  <div
                    key={guarantee.loanNumber}
                    className="flex items-center justify-between gap-2 rounded-xl border border-border px-3.5 py-2.5"
                  >
                    <div>
                      <p className="numeric text-sm font-semibold text-foreground">
                        {guarantee.loanNumber}
                      </p>
                      <StatusBadge map={LOAN_STATUS} status={guarantee.status} />
                    </div>
                    <p className="numeric text-sm font-semibold text-foreground">
                      {money(guarantee.outstanding)}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {confirmingDelete && (
        <DeleteMemberModal
          memberId={id}
          name={data.name}
          onClose={() => setConfirmingDelete(false)}
          onDeleted={() => navigate('/admin/members', { replace: true })}
        />
      )}
    </div>
  );
}

/**
 * The KYC file.
 *
 * Shown whatever the status. An approved profile is exactly the record a
 * reviewer needs to look back at when the same person applies again.
 */
function KycPanel({ kyc }: { kyc: KycDetail | undefined }) {
  if (!kyc) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>KYC</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-foreground-secondary">
            This member has not started their KYC, or you do not have permission to view it.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3">
        <CardTitle>KYC</CardTitle>
        <StatusBadge map={KYC_STATUS} status={kyc.status} />
      </CardHeader>
      <CardContent className="pt-0">
        {kyc.rejectionReason && (
          <Alert tone="danger" className="mb-4">
            {kyc.rejectionReason}
          </Alert>
        )}

        <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <DetailRow label="Full legal name" value={kyc.fullLegalName} />
          <DetailRow
            label="Date of birth"
            value={kyc.dateOfBirth ? formatLongDate(new Date(kyc.dateOfBirth)) : null}
          />
          <DetailRow label="Gender" value={kyc.gender?.toLowerCase()} />
          <DetailRow
            label="Identification"
            value={
              kyc.identificationType
                ? `${kyc.identificationType.replace(/_/g, ' ').toLowerCase()} · ${
                    kyc.identificationNumberMasked ?? 'hidden'
                  }`
                : null
            }
          />
          <DetailRow label="Address" value={kyc.residentialAddress} className="sm:col-span-2" />
          <DetailRow
            label="City / LGA / State"
            value={[kyc.city, kyc.lga, kyc.state].filter(Boolean).join(', ') || null}
          />
          <DetailRow
            label="Employment"
            value={
              kyc.employmentStatus
                ? (EMPLOYMENT_STATUS_LABELS[
                    kyc.employmentStatus as keyof typeof EMPLOYMENT_STATUS_LABELS
                  ] ?? kyc.employmentStatus)
                : null
            }
          />
          <DetailRow label="Organisation" value={kyc.organisation} />
          <DetailRow label="Job title" value={kyc.jobTitle} />
          <DetailRow label="Monthly income" value={kyc.monthlyIncomeBand} />
          <DetailRow
            label="Submitted"
            value={kyc.submittedAt ? formatLongDate(new Date(kyc.submittedAt)) : 'Not submitted'}
          />
          <DetailRow
            label="Approved"
            value={kyc.approvedAt ? formatLongDate(new Date(kyc.approvedAt)) : null}
          />
        </div>

        {kyc.nextOfKin && (
          <div className="mt-5 border-t border-border pt-5">
            <p className="mb-3 text-sm font-bold text-foreground">Next of kin</p>
            <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              <DetailRow label="Name" value={kyc.nextOfKin.fullName} />
              <DetailRow label="Relationship" value={kyc.nextOfKin.relationship} />
              <DetailRow label="Telephone" value={kyc.nextOfKin.phone} numeric />
              <DetailRow label="Email" value={kyc.nextOfKin.email} />
              <DetailRow label="Address" value={kyc.nextOfKin.address} className="sm:col-span-2" />
            </div>
          </div>
        )}

        {kyc.documents.length > 0 && (
          <div className="mt-5 border-t border-border pt-5">
            <p className="mb-3 text-sm font-bold text-foreground">Documents</p>
            <div className="space-y-2">
              {kyc.documents.map((document) => (
                <div
                  key={document.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-3.5 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {document.documentType.replace(/_/g, ' ').toLowerCase()}
                    </p>
                    <p className="truncate text-xs text-foreground-secondary">
                      {document.fileName} · {formatShortDate(new Date(document.uploadedAt))}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-foreground-secondary">
                    {document.status.toLowerCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {kyc.history.length > 0 && (
          <div className="mt-5 border-t border-border pt-5">
            <p className="mb-3 text-sm font-bold text-foreground">Review history</p>
            <div className="space-y-2">
              {kyc.history.map((entry, index) => (
                <div key={index} className="text-sm">
                  <span className="font-semibold text-foreground">
                    {entry.decision.replace(/_/g, ' ').toLowerCase()}
                  </span>{' '}
                  <span className="text-foreground-secondary">
                    by {entry.reviewer} · {formatLongDate(new Date(entry.at))}
                  </span>
                  {entry.reason && (
                    <p className="text-foreground-secondary">&ldquo;{entry.reason}&rdquo;</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function NotesPanel({ memberId, notes }: { memberId: string; notes: MemberDetail['notes'] }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');

  const add = useMutation({
    mutationFn: () => api.post(`/members/${memberId}/notes`, { body, isInternal: true }),
    onSuccess: () => {
      toast.success('Note added.');
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['member', memberId] });
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not add that note.'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notes</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="mb-4 flex gap-2">
          <Textarea
            rows={2}
            value={body}
            placeholder="Record something about this member's file"
            onChange={(e) => setBody(e.target.value)}
          />
          <Button
            className="self-end"
            disabled={body.trim().length < 3}
            loading={add.isPending}
            onClick={() => add.mutate()}
          >
            <MessageSquare size={15} /> Add
          </Button>
        </div>

        {notes.length === 0 ? (
          <p className="text-sm text-foreground-secondary">No notes on this file.</p>
        ) : (
          <div className="space-y-2">
            {notes.map((note) => (
              <div key={note.id} className="rounded-xl border border-border p-3.5">
                <p className="text-sm leading-relaxed text-foreground">{note.body}</p>
                <p className="mt-1.5 text-xs text-foreground-secondary">
                  {note.author} · {formatLongDate(new Date(note.createdAt))}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DetailRow({
  label,
  value,
  numeric,
  className,
}: {
  label: string;
  value: string | null | undefined;
  numeric?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-xs font-semibold uppercase tracking-wide text-foreground-secondary">
        {label}
      </p>
      <p className={cn('mt-0.5 text-sm text-foreground first-letter:uppercase', numeric && 'numeric')}>
        {value || <span className="text-foreground-secondary">Not provided</span>}
      </p>
    </div>
  );
}
